import React, { ReactNode } from "react";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import ClipLoader from "react-spinners/ClipLoader";
import ApiFailed from '../../Utils/ApiFailed';
import { CreateUrl, CallGetAPI } from 'Utils/ApiHelper.js';
import { APIGetLoggerReadingsModel, buildAPIGetLoggerReadingsModel } from "models/APIGetLoggerReadingsModel";
import { APIGetLoggerAlarmLevelModel, buildAPIGetLoggerAlarmLevelModel } from "models/APIGetLoggerAlarmLevelModel";

//Moment date/time formatting
//https://momentjs.com/docs/
import moment, { Duration } from 'moment';

import { withStyles, createStyles } from '@mui/styles';

//DevExtreme
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';
import Chart, { CommonSeriesSettings, ArgumentAxis, Series, Legend, ValueAxis, ZoomAndPan, ScrollBar, Tooltip, TickInterval, Aggregation, Point, Size, Title } from 'devextreme-react/chart';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';

const styles: Record<string, any> = {
    formControl: {
        minWidth: 120,
    },
    cardCategoryWhite: {
        "&,& a,& a:hover,& a:focus": {
            color: "rgba(255,255,255,.62)",
            margin: "0",
            fontSize: "14px",
            marginTop: "0",
            marginBottom: "0"
        },
        "& a,& a:hover,& a:focus": {
            color: "#FFFFFF"
        }
    },
    cardTitleWhite: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        color: "#FFFFFF",
        minHeight: "auto",
        fontWeight: "300",
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
        textDecoration: "none",
        "& small": {
            color: "#777",
            fontSize: "65%",
            fontWeight: "400",
            lineHeight: "1"
        }
    }
};

const style = createStyles(styles);

// Data to display on graph 
interface Readings {
    date: Date;
    channel: string;
    readingA: number | null;
    readingB: number | null;
    readingC: number | null;
    readingD: number | null;
    readingE: number | null;
    readingMax: number | null;
    readingMin: number | null;
    readingSet: number | null;
    readingClr: number | null;

}


interface Props {
    classes: {
        cardTitleWhite: string;
    };
    serial: string;
    startDate: Date;
    endDate: Date;
    channels: string[];   // Channels to display ('C', 'D' or 'E')
    isprimary: string;
    title: string;
    units: string;
    cache?: Array<APIGetLoggerReadingsModel>;        // readings for all channels
    updateCache?: (channel: string, readings: Array<APIGetLoggerReadingsModel>) => void;

}

interface State {
    loading: boolean;
    tableHidden: boolean;
    authorized: boolean;
    startDate: Date;
    endDate: Date;
    maxValue: number;
    minValue: number;
    readingsData: Array<APIGetLoggerReadingsModel>;
    graphData: Array<Readings> | undefined;
    channels: string[];   // Channel to display ('C', 'D' or 'E')
}

class CombinedGraphTL extends React.Component<Props, State> {
    constructor(props: Readonly<Props>) {
        super(props);
        this.state = {
            loading: true,
            tableHidden: true,
            authorized: true,
            maxValue: 0,
            minValue: Number.MAX_VALUE,
            startDate: props.startDate,
            endDate: props.endDate,
            readingsData: [],
            graphData: undefined,
            channels: [],
        };
    }

    componentDidMount(): void {

        console.log("CombinedGraphTL DidMount - " + this.props.channels);
        //get alarms for logger here
        if (this.reloadData(this.state.startDate, this.state.endDate)) {
            this.setState({ tableHidden: false });
        }
        else {
            this.setState({ tableHidden: true });
        }

    }

    componentDidUpdate(prevProps: Props): void {
        if (prevProps.startDate != this.props.startDate
            || prevProps.endDate != this.props.endDate
            || prevProps.channels != this.props.channels
        ) {
            console.log("CombinedGraphTL DidUpdate - " + this.props.channels);
            this.setState({
                startDate: this.props.startDate,
                endDate: this.props.endDate,
                channels: this.props.channels,
            });
            this.reloadData(this.props.startDate, this.props.endDate);
        }

    }

    reloadData(start: Date, end: Date): boolean {

        this.setState({ loading: true });

        let readingsData = new Array<APIGetLoggerReadingsModel>();
        const graphData = new Array<Readings>();

        let maxValue = 0;
        let minValue = Number.MAX_VALUE;

        //get alarms for logger here
        const me = this;

        const promises = new Array<Promise<any>>();
        if (this.props.cache == null || this.props.cache.length == 0) {
            // generate array of Promises each adding the readingsData for a logger
            promises.push(

                CallGetAPI(CreateUrl('/api/aquaguard/GetLogReadings?companyid=0&logger=' + this.props.serial + "&startDate=" + moment(start).format("yyyy-MM-DD") + "&endDate=" + moment(end).format("yyyy-MM-DD") + "T23:59:59"), {})
                    .then(json => {
                        readingsData = readingsData.concat(buildAPIGetLoggerReadingsModel(json));
                    })
                    .catch(function (ex) {
                        me.setState(
                            {
                                tableHidden: true,
                                readingsData: [],
                                loading: false,
                            });
                        console.log(ex);
                    })
            );
        }
        else {
            readingsData = this.props.cache.slice();
            // Dummy promise
            promises.push(new Promise(function (resolve, reject) {
                            resolve();
                        }
                ))
        }

        Promise.all(promises).then(() => {

            // save readings to cache in parent component
            if (this.props.updateCache) {
                this.props.updateCache("", readingsData);
            }

            readingsData.forEach((v: APIGetLoggerReadingsModel) => {
                if (v.dateStamp != null) {

                    let value = v.value;
                    if (value == null)
                        value = v.value2;

                    if (v.channelletter == 'A') {
                        graphData.push({
                            date: new Date(v.dateStamp),
                            channel: "Reading",
                            readingA: (v.value != -32768) ? value : null,
                            readingB: null,
                            readingC: null,
                            readingD: null,
                            readingE: null,
                            readingMax: (v.maxValue != -32768) ? v.maxValue : null,
                            readingMin: (v.minValue != -32768) ? v.minValue : null,
                            readingSet: null,
                            readingClr: null,
                        });
                        if (value != -32768) {
                            if (value != null && value > maxValue) {
                                maxValue = value;
                            }
                            if (value != null && value < minValue) {
                                minValue = value;
                            }
                        }
                    }
                    if (v.channelletter == 'B') {
                        graphData.push({
                            date: new Date(v.dateStamp),
                            channel: "Reading",
                            readingA: null,
                            readingB: (value != -32768) ? value : null,
                            readingC: null,
                            readingD: null,
                            readingE: null,
                            readingMax: (v.maxValue != -32768) ? v.maxValue : null,
                            readingMin: (v.minValue != -32768) ? v.minValue : null,
                            readingSet: null,
                            readingClr: null,
                        });
                        if (value != -32768) {
                            if (value != null && value > maxValue) {
                                maxValue = value;
                            }
                            if (value != null && value < minValue) {
                                minValue = value;
                            }
                        }
                    }

                    if (v.channelletter == 'C') {
                        graphData.push({
                            date: new Date(v.dateStamp),
                            channel: "Reading",
                            readingA: null,
                            readingB: null,
                            readingC: (value != -32768) ? value : null,
                            readingD: null,
                            readingE: null,
                            readingMax: (v.maxValue != -32768) ? v.maxValue : null,
                            readingMin: (v.minValue != -32768) ? v.minValue : null,
                            readingSet: null,
                            readingClr: null,
                        });
                        if (value != -32768) {
                            if (value != null && value > maxValue) {
                                maxValue = value;
                            }
                            if (value != null && value < minValue) {
                                minValue = value;
                            }
                        }
                    }
                    if (v.channelletter == 'D') {
                        graphData.push({
                            date: new Date(v.dateStamp),
                            channel: "Reading",
                            readingA: null,
                            readingB: null,
                            readingC: null,
                            readingD: (value != -32768) ? value : null,
                            readingE: null,
                            readingMax: (v.maxValue != -32768) ? v.maxValue : null,
                            readingMin: (v.minValue != -32768) ? v.minValue : null,
                            readingSet: null,
                            readingClr: null,
                        });
                        if (value != -32768) {
                            if (value != null && value > maxValue) {
                                maxValue = value;
                            }
                            if (value != null && value < minValue) {
                                minValue = value;
                            }
                        }
                    }

                    if (v.channelletter == 'E') {
                        graphData.push({
                            date: new Date(v.dateStamp),
                            channel: "Reading",
                            readingA: null,
                            readingB: null,
                            readingC: null,
                            readingD: null,
                            readingE: (value != -32768) ? value : null,
                            readingMax: (v.maxValue != -32768) ? v.maxValue : null,
                            readingMin: (v.minValue != -32768) ? v.minValue : null,
                            readingSet: null,
                            readingClr: null,
                        });
                        if (value != -32768) {
                            if (value != null && value > maxValue) {
                                maxValue = value;
                            }
                            if (value != null && value < minValue) {
                                minValue = value;
                            }
                        }
                    }

                }
            });

            graphData.sort((a, b) => a.date.getTime() - b.date.getTime());

            console.log("Channel " + me.props.channels + " Min: " + minValue + " Max: " + maxValue);

            me.setState({
                minValue: minValue,
                maxValue: maxValue,
                readingsData: readingsData,
                graphData: graphData,
                loading: false,
            });

        });



    return true;
}

    onExporting(e: any): void {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Alarms');

        exportDataGrid({
            component: e.component,
            worksheet: worksheet
        }).then(function () {
            workbook.xlsx.writeBuffer()
                .then(function (buffer: Buffer) {
                    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Aquaguard Export.xlsx');
                });
        });
        e.cancel = true;
    }

    dateColumnCustomizeText(cellInfo: any): string {
        if (cellInfo.value == null)
            return "";
        else
            return moment(cellInfo.value).format("HH:mm DD/MM/YYYY");
    }

    legendClickHandler(e: any) {
        const series = e.target;
        if (series.isVisible()) {
            series.hide();
        } else {
            series.show();
        }
    }


    render(): ReactNode {

        return (

            <div>
                {this.state.loading &&
                    <div style={{
                        position: 'relative', left: '40%', top: '50%',
                    }}>
                        <ClipLoader
                            size={50}
                            color={"#123abc"}
                            loading={this.state.loading}
                        />
                    </div>
                }
                {!this.state.loading &&
                    <div>
                        {this.state.tableHidden &&
                               <ApiFailed />
                        }
                    {!this.state.tableHidden &&
                        <GridContainer>
                            <GridItem xs={12} sm={12} md={12}>
                            <p style={{ fontWeight: "bold", fontSize: "10pt", textAlign: "center" }}>
                                {this.props.title == "" ?
                                    <span>Channel {this.props.channels}</span>
                                    :
                                    <span>{this.props.title}</span>
                                }
                            </p>
                            <Chart onLegendClick={this.legendClickHandler} dataSource={this.state.graphData}>
                                <Size height={this.props.isprimary == "yes" ? 500 : 130} width="100%" />
                                    <CommonSeriesSettings
                                        argumentField="date"
                                        type="spline"
                                        ignoreEmptyPoints={true}>
                                        <Point size={4} />
                                    </CommonSeriesSettings>
                                {this.props.channels && this.props.channels.map((c, i) => {
                                    switch (c.substr(0, 1)) {
                                        case "A":
                                            return <Series key={i} valueField="readingA" name={c} color="blue" point={{ visible: false }} />;
                                        case "B":
                                            return <Series key={i} valueField="readingB" name={c} color="green" point={{ visible: false }} />;
                                        case "C":
                                            return <Series key={i} valueField="readingC" name={c} color="red" point={{ visible: false }} />;
                                        case "D":
                                            return <Series key={i} valueField="readingD" name={c} color="orange" point={{ visible: false }} />;
                                        case "E":
                                            return <Series key={i} valueField="readingE" name={c} color="purple" point={{ visible: false }} />;
                                    } 
                                    return <span key={i}></span>
                                    })
                                }
                                <ValueAxis
                                    name="Value"
                                    visualRange={{ startValue: this.state.minValue, endValue: this.state.maxValue }}
                                    allowDecimals={true}
                                    title={{ text: this.props.units, font: { size: this.props.isprimary == "yes" ? 12 : 9 } }}
                                />
                                   <ArgumentAxis
                                        allowDecimals={false}
                                        argumentType="datetime"
                                        minValueMargin={0.1}
                                        maxValueMargin={0.1}
                                        wholeRange={[this.state.startDate, this.state.endDate]}
                                        >
                                    <TickInterval days={1} />
                                    </ArgumentAxis>
                                {this.props.isprimary == "yes" &&
                                    <ZoomAndPan
                                        argumentAxis="both"
                                        valueAxis="none"
                                    />
                                }
                                    <ScrollBar
                                    visible={this.props.isprimary == "yes"}
                                    />
                                <Legend visible={this.props.isprimary == "yes"} />
                                <Tooltip enabled={this.props.isprimary == "yes"} />
                                </Chart>
                            </GridItem>
                        </GridContainer>

                    }
                    </div>
                }

            </div>

        )
    }

}

export default withStyles(style)(CombinedGraphTL);